import React, { useState } from 'react';
import {
  Modal,
  Box,
  SpaceBetween,
  Button,
  FormField,
  Input,
  Alert,
} from '@cloudscape-design/components';
import { createOpenPOExport, CreateExportInput } from 'src/api/hooks/createOpenPOExport';
import { useFlashbar } from 'src/common/hooks/useFlashbar';

interface OpenPOExportModalProps {
  controlId: string;
  onClose: () => void;
  onExportCreated: () => void;
}

export const OpenPOExportModal: React.FC<OpenPOExportModalProps> = ({
  controlId,
  onClose,
  onExportCreated,
}) => {
  const [exportName, setExportName] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const { addFlashbarMessage } = useFlashbar();

  const handleCreateExport = async () => {
    if (!exportName.trim()) {
      addFlashbarMessage({
        type: 'error',
        header: 'Validation Error',
        content: 'Export name is required.',
        dismissible: true,
        id: 'export_name_error',
      });
      return;
    }

    setIsLoading(true);
    try {
      await createOpenPOExport({
        controlId,
        exportName: exportName.trim(),
        filters: {
          cost_center: '1173',
          company: '2D',
        },
      });

      addFlashbarMessage({
        type: 'success',
        header: 'Export Created',
        content: `Export "${exportName}" has been created successfully.`,
        dismissible: true,
        id: 'export_success',
      });

      onExportCreated();
      onClose();
    } catch (error) {
      console.error('Error creating export:', error);
      addFlashbarMessage({
        type: 'error',
        header: 'Export Failed',
        content: 'Failed to create export. Please try again.',
        dismissible: true,
        id: 'export_error',
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Modal
      onDismiss={onClose}
      visible={true}
      closeAriaLabel="Close modal"
      footer={
        <Box float="right">
          <SpaceBetween direction="horizontal" size="xs">
            <Button variant="link" onClick={onClose}>
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={handleCreateExport}
              loading={isLoading}
              disabled={!exportName.trim()}
            >
              Create Export
            </Button>
          </SpaceBetween>
        </Box>
      }
      header="Create Open PO Export"
    >
      <SpaceBetween size="m">
        <Alert>
          This will create an export for Open PO data (GRC18956). The export will be processed and
          available for download once completed.
        </Alert>

        <FormField label="Export Name" description="Enter a name for your export">
          <Input
            value={exportName}
            onChange={({ detail }) => setExportName(detail.value)}
            placeholder="Enter export name"
            disabled={isLoading}
          />
        </FormField>

        <FormField label="Control ID" description="The control ID for this export">
          <Input value={controlId} disabled={true} />
        </FormField>
      </SpaceBetween>
    </Modal>
  );
};
